var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Type } from 'class-transformer';
import { IsArray, IsBoolean, IsDateString, IsEmail, IsEnum, IsInt, IsNotEmpty, IsOptional, IsString, IsUUID, MaxLength, Min, ValidateNested, } from 'class-validator';
import { PatientBookingPayerContext } from '../booking-intake.js';
import { PrescriptionFrequency } from '../../treatment-plan/enums/prescription-frequency.enum.js';
/**
 * One requested Heilmittel in a GKV manual intake (RS-009): a treatment concept
 * (+ its display name for the snapshot) with its Anzahl. There is no free-text
 * field — the concept id comes from the practice's offering.
 */
export class FirstContactTreatmentDto {
    treatmentConceptId;
    treatmentName;
    count;
}
__decorate([
    IsUUID(),
    __metadata("design:type", String)
], FirstContactTreatmentDto.prototype, "treatmentConceptId", void 0);
__decorate([
    IsString(),
    __metadata("design:type", String)
], FirstContactTreatmentDto.prototype, "treatmentName", void 0);
__decorate([
    IsInt(),
    Min(1),
    IsOptional(),
    __metadata("design:type", Number)
], FirstContactTreatmentDto.prototype, "count", void 0);
/**
 * Contact details a WEBSITE (embed) visitor supplies (PRD #1358, RS-004). Only
 * the public submission sends this; the authenticated `me` flow omits it (the
 * booker is the logged-in patient). Name + phone + email are snapshotted on the
 * booking's intake payload — never the person row — and the practice calls back
 * on the phone (collected, not verified) once the email link is confirmed.
 */
export class FirstContactContactDto {
    firstName;
    lastName;
    email;
    phone;
}
__decorate([
    IsString(),
    IsNotEmpty(),
    MaxLength(255),
    __metadata("design:type", String)
], FirstContactContactDto.prototype, "firstName", void 0);
__decorate([
    IsString(),
    IsNotEmpty(),
    MaxLength(255),
    __metadata("design:type", String)
], FirstContactContactDto.prototype, "lastName", void 0);
__decorate([
    IsEmail(),
    MaxLength(255),
    __metadata("design:type", String)
], FirstContactContactDto.prototype, "email", void 0);
__decorate([
    IsString(),
    IsNotEmpty(),
    MaxLength(64),
    __metadata("design:type", String)
], FirstContactContactDto.prototype, "phone", void 0);
/**
 * Patient request to place a single pending hold for a NEW episode (PRD #1343,
 * RS-008 non-GKV, RS-009 GKV manual). The patient sends the payer context, the
 * primary treatment concept (+ its display name for the intake snapshot) and the
 * chosen slot start. The office location, a concrete free therapist and the
 * duration are all resolved and re-validated server-side; the chosen `start` is
 * re-checked against a fresh slot search and returns a typed `409` conflict if
 * it was taken meanwhile (see {@link PatientBookingConflictCode}).
 *
 * For a GKV manual booking the patient also sends the structured prescription
 * intake — one or more Heilmittel with their Anzahl ({@link treatments}), the
 * weekly {@link frequency} and the {@link urgentTreatmentNeeded} flag; the
 * `treatmentConceptId`/`treatmentName` are the primary (first) Heilmittel.
 *
 * Exactly ONE appointment is created and NO treatment plan — the plan is born at
 * the practice's confirm step, from the plan form prefilled with this intake.
 */
export class CreateFirstContactBookingDto {
    payerContext;
    treatmentConceptId;
    treatmentName;
    /** Optional requested count (Anzahl); non-GKV counts are optional. */
    count;
    start;
    /**
     * GKV manual intake (RS-009): every requested Heilmittel with its Anzahl,
     * including the primary. Omitted for a non-GKV treatment-select booking.
     */
    treatments;
    /** GKV manual intake: the prescribed weekly Therapiefrequenz. */
    frequency;
    /** GKV manual intake: dringlicher Behandlungsbedarf. */
    urgentTreatmentNeeded;
    /**
     * Folgeverordnung (RS-011): the predecessor plan this new episode continues.
     * Set when the booking was started from an exhausted/expiring plan card — the
     * hold is scoped to that episode's continuity therapist and carries the
     * reference for the practice. Omitted for an ordinary first-contact booking.
     */
    predecessorPlanId;
    /**
     * WEBSITE (embed) submission only (PRD #1358, RS-004): the visitor's contact.
     * The public controller requires it; the authenticated `me` flow omits it. Its
     * presence is what makes the created hold a website hold — the person is born
     * credential-less (null email) and this rides the intake payload.
     */
    contact;
    /**
     * WEBSITE (embed) submission only: the visitor ticked the DSGVO consent shown
     * before submission ({@link EMBED_BOOKING_CONSENT_TEXT}). The public
     * controller refuses the booking unless this is `true`.
     */
    consentGiven;
    /**
     * WEBSITE (embed) submission only (PRD #1358, RS-009): the solved altcha
     * proof-of-work payload (base64). The public controller gates the submission
     * POST on it — the session exchange is deliberately NOT gated. Omitted by the
     * authenticated `me` flow.
     */
    altcha;
}
__decorate([
    IsEnum(PatientBookingPayerContext),
    __metadata("design:type", String)
], CreateFirstContactBookingDto.prototype, "payerContext", void 0);
__decorate([
    IsUUID(),
    __metadata("design:type", String)
], CreateFirstContactBookingDto.prototype, "treatmentConceptId", void 0);
__decorate([
    IsString(),
    __metadata("design:type", String)
], CreateFirstContactBookingDto.prototype, "treatmentName", void 0);
__decorate([
    IsInt(),
    Min(1),
    IsOptional(),
    __metadata("design:type", Number)
], CreateFirstContactBookingDto.prototype, "count", void 0);
__decorate([
    IsDateString(),
    __metadata("design:type", String)
], CreateFirstContactBookingDto.prototype, "start", void 0);
__decorate([
    IsArray(),
    ValidateNested({ each: true }),
    Type(() => FirstContactTreatmentDto),
    IsOptional(),
    __metadata("design:type", Array)
], CreateFirstContactBookingDto.prototype, "treatments", void 0);
__decorate([
    IsEnum(PrescriptionFrequency),
    IsOptional(),
    __metadata("design:type", String)
], CreateFirstContactBookingDto.prototype, "frequency", void 0);
__decorate([
    IsBoolean(),
    IsOptional(),
    __metadata("design:type", Boolean)
], CreateFirstContactBookingDto.prototype, "urgentTreatmentNeeded", void 0);
__decorate([
    IsUUID(),
    IsOptional(),
    __metadata("design:type", String)
], CreateFirstContactBookingDto.prototype, "predecessorPlanId", void 0);
__decorate([
    ValidateNested(),
    Type(() => FirstContactContactDto),
    IsOptional(),
    __metadata("design:type", FirstContactContactDto)
], CreateFirstContactBookingDto.prototype, "contact", void 0);
__decorate([
    IsBoolean(),
    IsOptional(),
    __metadata("design:type", Boolean)
], CreateFirstContactBookingDto.prototype, "consentGiven", void 0);
__decorate([
    IsString(),
    IsOptional(),
    __metadata("design:type", String)
], CreateFirstContactBookingDto.prototype, "altcha", void 0);
//# sourceMappingURL=create-first-contact-booking.dto.js.map